import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invoicesAPI, customersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AsyncSearchableSelect } from '../components/ui/async-searchable-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft, Loader2, Plus, Trash2, Save, Edit2, X, Receipt, User,
  DollarSign, Mail, Send, CheckCircle2, Ban, Copy, ExternalLink, Download, BookOpen,
  Banknote, Clock, AlertCircle, Smartphone, QrCode, Link2, Share2, CreditCard, History, RefreshCw, MoreVertical,
  Briefcase, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { QRCodeSVG } from 'qrcode.react';
import { PriceBookPicker } from '../components/PriceBookPicker';
import { LocationPicker, LocationBadge } from '../components/LocationPicker';
import { BillingBadge } from '../components/BillingBadge';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  { value: 'partially_paid', label: 'Partial', color: 'bg-amber-100 text-amber-700' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700' },
  { value: 'void', label: 'Void', color: 'bg-red-100 text-red-700' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH / Bank Transfer' },
  { value: 'square', label: 'Square' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'other', label: 'Other' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} text-xs`} data-testid={`status-badge-${status}`}>{opt.label}</Badge>;
}

const PAYOUT_STYLES = {
  processing: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  available: { icon: Banknote, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  in_transit: { icon: Banknote, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-200' },
  deposited: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  failed: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
};

function TakePaymentContent({ invoice, onClose }) {
  const paymentUrl = `${window.location.origin}/invoice/${invoice.invoice_token}`;
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(paymentUrl); setCopied(true); toast.success('Payment link copied'); setTimeout(() => setCopied(false), 2000); };
  const handleShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: `Invoice ${invoice.invoice_number}`, text: `Pay invoice ${invoice.invoice_number}`, url: paymentUrl }); } catch { handleCopy(); } } else { handleCopy(); }
  };
  const displayAmount = invoice.balance_due > 0 ? invoice.balance_due : invoice.total;
  return (
    <div className="space-y-5 py-2 overflow-hidden" data-testid="take-payment-content">
      <div className="text-center">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Amount Due</p>
        <p className="text-3xl font-bold" data-testid="payment-qr-amount">${parseFloat(displayAmount || 0).toFixed(2)}</p>
        <p className="text-xs text-muted-foreground">{invoice.invoice_number}</p>
      </div>
      <div className="flex justify-center" data-testid="payment-qr-code">
        <div className="bg-white p-3 sm:p-4 rounded-xl border shadow-sm max-w-[200px]">
          <QRCodeSVG value={paymentUrl} size={170} level="M" style={{ width: '100%', height: 'auto' }} />
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground">Customer scans to pay with card, ACH, Apple Pay, or Google Pay</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button variant="outline" className="flex-1 gap-2" onClick={handleCopy} data-testid="copy-payment-link-btn">
          {copied ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy Link'}
        </Button>
        <Button className="flex-1 gap-2" onClick={handleShare} data-testid="share-payment-link-btn"><Share2 className="w-4 h-4" /> Share</Button>
      </div>
      <div className="rounded-md border bg-muted/30 p-2 overflow-hidden">
        <p className="text-[11px] text-muted-foreground truncate font-mono" title={paymentUrl} data-testid="payment-link-url">{paymentUrl}</p>
      </div>
    </div>
  );
}

function PayoutStatusCard({ data }) {
  const status = data.payout_status || 'processing';
  const display = data.display || {};
  const style = PAYOUT_STYLES[status] || PAYOUT_STYLES.processing;
  const Icon = style.icon;
  return (
    <Card className={`border ${style.bg}`} data-testid="payout-status-card">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Icon className={`w-4 h-4 ${style.color}`} /> Deposit Status</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Badge className={`${style.bg} ${style.color} text-xs border`} data-testid="payout-status-badge">Paid &middot; {display.label || status}</Badge>
        <p className="text-xs text-muted-foreground">{display.description}</p>
        {data.paid_at && <p className="text-xs text-muted-foreground">Paid: {new Date(data.paid_at).toLocaleDateString()}</p>}
        {data.payout_arrival_date && <p className="text-xs text-muted-foreground">{status === 'deposited' ? 'Deposited' : 'Expected'}: {new Date(data.payout_arrival_date).toLocaleDateString()}</p>}
        {data.platform_fee_cents > 0 && <p className="text-[11px] text-muted-foreground">Platform fee: ${(data.platform_fee_cents / 100).toFixed(2)}</p>}
      </CardContent>
    </Card>
  );
}

function AddPaymentModal({ open, onOpenChange, invoice, onSuccess }) {
  const [form, setForm] = useState({ amount: '', payment_method: 'cash', reference_number: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const balanceDue = parseFloat(invoice?.balance_due || 0);

  useEffect(() => {
    if (open) setForm({ amount: balanceDue > 0 ? balanceDue.toFixed(2) : '', payment_method: 'cash', reference_number: '', notes: '' });
  }, [open, balanceDue]);

  const handleSubmit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await invoicesAPI.addPayment(invoice.id, { amount, payment_method: form.payment_method, reference_number: form.reference_number || undefined, notes: form.notes || undefined });
      toast.success(`Payment of $${amount.toFixed(2)} recorded`);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> Record Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {balanceDue > 0 && (
            <div className="text-center p-3 rounded-lg bg-muted/50 border">
              <p className="text-xs text-muted-foreground">Balance Due</p>
              <p className="text-2xl font-bold" data-testid="payment-modal-balance">${balanceDue.toFixed(2)}</p>
            </div>
          )}
          <div>
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="payment-amount-input" />
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference # <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="Check #, receipt #..." data-testid="payment-reference-input" />
          </div>
          <div>
            <Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Payment notes..." data-testid="payment-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="confirm-add-payment-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1" />} Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidModal({ open, onOpenChange, invoice, onSuccess }) {
  const [form, setForm] = useState({ payment_method: 'cash', reference_number: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const balanceDue = parseFloat(invoice?.balance_due || 0);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await invoicesAPI.markPaid(invoice.id, { payment_method: form.payment_method, reference_number: form.reference_number || undefined, notes: form.notes || undefined });
      toast.success('Invoice marked as paid');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark paid');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mark-paid-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-600" /> Mark Invoice Paid</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="text-center p-3 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <p className="text-xs text-muted-foreground">Recording full payment of</p>
            <p className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="mark-paid-amount">${balanceDue.toFixed(2)}</p>
          </div>
          <div>
            <Label>Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="markpaid-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reference # <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="Check #, receipt #..." data-testid="markpaid-reference-input" />
          </div>
          <div>
            <Label>Notes <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Payment notes..." data-testid="markpaid-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="confirm-mark-paid-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Mark Paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentMethodBadge({ method }) {
  const label = PAYMENT_METHODS.find(m => m.value === method)?.label || (method === 'stripe' ? 'Stripe' : method || 'Other');
  const isStripe = method === 'stripe';
  return (
    <Badge variant="outline" className={`text-[10px] ${isStripe ? 'border-violet-300 text-violet-700 dark:text-violet-400' : ''}`}>
      {isStripe && <CreditCard className="w-2.5 h-2.5 mr-0.5" />}{label}
    </Badge>
  );
}

export default function InvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = (isOwner || isOffice);
  const isNew = id === 'new';

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [newItem, setNewItem] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemForm, setItemForm] = useState({});

  const [showSendEmail, setShowSendEmail] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [attachPdf, setAttachPdf] = useState(true);
  const [pbPickerOpen, setPbPickerOpen] = useState(false);

  const [payoutData, setPayoutData] = useState(null);
  const [showPaymentQR, setShowPaymentQR] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);

  // Auto-create for /invoices/new
  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await invoicesAPI.create({});
        if (cancelled) return;
        navigate(`/invoices/${res.data.id}`, { replace: true });
      } catch {
        toast.error('Failed to create invoice');
        navigate('/invoices');
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, navigate]);

  const isEditable = invoice && invoice.status !== 'paid' && invoice.status !== 'void';
  const canAddPayment = invoice && !['paid', 'void', 'draft'].includes(invoice.status) && invoice.balance_due > 0;
  const canTakeOnlinePayment = invoice && invoice.stripe_connected && invoice.invoice_token && !['paid', 'void', 'draft'].includes(invoice.status) && invoice.balance_due > 0;

  const fetchJobsForCustomer = useCallback(async (customerId) => {
    if (!customerId) { setCustomerJobs([]); return; }
    setLoadingJobs(true);
    try {
      const res = await invoicesAPI.jobsForCustomer(customerId);
      setCustomerJobs(res.data || []);
    } catch { setCustomerJobs([]); }
    setLoadingJobs(false);
  }, []);

  const fetchInvoice = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await invoicesAPI.get(id);
      setInvoice(res.data);
      if (res.data.customer_id) {
        try { const cres = await customersAPI.get(res.data.customer_id); setCustomer(cres.data); } catch { setCustomer(null); }
        fetchJobsForCustomer(res.data.customer_id);
      }
      if (res.data.status === 'paid') {
        try { const pres = await invoicesAPI.payoutStatus(id); setPayoutData(pres.data); } catch {}
      }
    } catch {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally { setLoading(false); }
  }, [id, isNew, navigate, fetchJobsForCustomer]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const updateField = async (field, value) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      // When customer changes, reset job
      const payload = { [field]: value };
      if (field === 'customer_id') {
        payload.job_id = '';  // clear job linkage
      }
      const res = await invoicesAPI.update(id, payload);
      setInvoice(res.data);
      if (field === 'customer_id' && value) {
        try { const cres = await customersAPI.get(value); setCustomer(cres.data); } catch { setCustomer(null); }
        fetchJobsForCustomer(value);
      } else if (field === 'customer_id' && !value) {
        setCustomer(null);
        setCustomerJobs([]);
      }
    } catch { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  const searchCustomers = async (q) => {
    try { const res = await customersAPI.list({ search: q, page_size: 20 }); return (res.data.customers || []).map(c => ({ value: c.id, label: c.display_name })); } catch { return []; }
  };

  // Normalise empty-string form values to null before sending. Pydantic
  // won't coerce "" to a number, so any blank field would 422.
  const sanitizeItemPayload = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const NULLABLE = ['description', 'quantity', 'unit_price', 'discount_percent', 'sort_order'];
    const out = { ...raw };
    for (const f of NULLABLE) if (out[f] === '' || out[f] === undefined) out[f] = null;
    return out;
  };

  const startNewItem = (type) => { setNewItem({ type, name: '', description: '', quantity: 1, unit_price: 0, taxable: true, discount_percent: 0 }); setEditingItemId(null); };
  const saveNewItem = async () => { if (!newItem?.name) return; try { await invoicesAPI.addItem(id, sanitizeItemPayload(newItem)); setNewItem(null); fetchInvoice(); } catch { toast.error('Failed to add item'); } };
  const startEditItem = (item) => { setEditingItemId(item.id); setItemForm({ type: item.type, name: item.name, description: item.description || '', quantity: item.quantity, unit_price: item.unit_price, taxable: item.taxable !== false, discount_percent: item.discount_percent || 0 }); setNewItem(null); };
  const saveEditItem = async () => { try { await invoicesAPI.updateItem(editingItemId, sanitizeItemPayload(itemForm)); setEditingItemId(null); fetchInvoice(); } catch { toast.error('Failed to update item'); } };
  const removeItem = async (itemId) => { try { await invoicesAPI.removeItem(itemId); fetchInvoice(); } catch { toast.error('Failed to remove item'); } };

  const handleDownloadPdf = async () => {
    try { const res = await invoicesAPI.downloadPdf(id); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = `${invoice.invoice_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url); } catch { toast.error('Failed to generate PDF'); }
  };

  const handleVoid = async () => { try { const res = await invoicesAPI.voidInvoice(id); setInvoice(res.data); toast.success('Invoice voided'); } catch { toast.error('Failed to void invoice'); } };

  // Payment reversal state
  const [reverseTarget, setReverseTarget] = useState(null); // payment object to reverse
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  const fetchAuditLog = useCallback(async () => {
    try { const res = await invoicesAPI.auditLog(id); setAuditLog(res.data?.entries || []); } catch { /* non-critical */ }
  }, [id]);

  useEffect(() => { if (invoice) fetchAuditLog(); }, [invoice, fetchAuditLog]);

  const handleReversePayment = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    try {
      const res = await invoicesAPI.reversePayment(id, reverseTarget.id, { reason: reverseReason || null });
      toast.success(`Payment of $${res.data.reversed_amount.toFixed(2)} reversed — invoice is now ${res.data.invoice_status}`);
      setReverseTarget(null);
      setReverseReason('');
      fetchInvoice();
      fetchAuditLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reverse payment');
    } finally { setReversing(false); }
  };

  const handleCorrectStatus = async () => {
    try {
      const res = await invoicesAPI.correctStatus(id);
      toast.success(`Status corrected: ${res.data.old_status} → ${res.data.new_status}`);
      fetchInvoice();
      fetchAuditLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to correct status');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await invoicesAPI.deletePayment(id, paymentId);
      toast.success('Payment deleted');
      fetchInvoice();
      fetchAuditLog();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete payment'); }
  };

  const handleSendEmail = async () => {
    if (!sendEmail || !sendEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try { await invoicesAPI.send(id, { to: sendEmail, attach_pdf: attachPdf }); toast.success(`Invoice sent to ${sendEmail}`); setShowSendEmail(false); setSendEmail(''); fetchInvoice(); } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send'); } finally { setSending(false); }
  };

  const handleCopyLink = () => { if (!invoice?.invoice_token) return; navigator.clipboard.writeText(`${window.location.origin}/invoice/${invoice.invoice_token}`); toast.success('Link copied'); };

  if (loading || isNew) return <Layout><div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /><span className="ml-2 text-sm text-muted-foreground">{isNew ? 'Creating invoice...' : 'Loading...'}</span></div></Layout>;
  if (!invoice) return null;

  const items = invoice.items || [];
  const payments = invoice.payments || [];

  return (
    <Layout>
      <TooltipProvider>
        <div className="space-y-6" data-testid="invoice-editor">
          {/* Sticky Context Bar */}
          <div className="sticky top-14 z-30 -mx-4 px-4 md:-mx-6 md:px-6 pb-3 pt-1 bg-background/95 backdrop-blur-sm border-b border-border/50 shadow-[0_2px_8px_rgba(0,0,0,0.06)]" data-testid="invoice-sticky-bar">
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate('/invoices')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
              <h1 className="text-lg font-bold truncate" data-testid="invoice-number">{invoice.invoice_number}</h1>
              <StatusBadge status={invoice.status} />
              {invoice.notes && invoice.notes.startsWith('Auto-generated from maintenance plan') && (
                <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200 gap-1" variant="outline" data-testid="invoice-maintenance-badge">
                  <RefreshCw className="w-3 h-3" /> Maintenance Plan
                </Badge>
              )}
              {(invoice.customer_name || customer?.display_name) && (
                <span className="hidden md:inline text-sm text-muted-foreground truncate max-w-[200px]">{invoice.customer_name || customer?.display_name}</span>
              )}
              {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Desktop: show all action buttons */}
              <div className="hidden sm:flex items-center gap-2 flex-wrap">
                <Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleDownloadPdf} data-testid="download-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" /> PDF</Button>
                </TooltipTrigger><TooltipContent>Download invoice as PDF</TooltipContent></Tooltip>

                {canAddPayment && canEdit && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowAddPayment(true)} data-testid="add-payment-btn">
                      <DollarSign className="w-3.5 h-3.5 mr-1" /> Add Payment
                    </Button>
                  </TooltipTrigger><TooltipContent>Record a manual payment</TooltipContent></Tooltip>
                )}

                {canAddPayment && canEdit && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowMarkPaid(true)} data-testid="mark-paid-btn">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
                    </Button>
                  </TooltipTrigger><TooltipContent>Record full remaining payment</TooltipContent></Tooltip>
                )}

                {canTakeOnlinePayment && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700" onClick={() => setShowPaymentQR(true)} data-testid="take-payment-btn">
                      <Smartphone className="w-3.5 h-3.5 mr-1" /> Take Payment
                    </Button>
                  </TooltipTrigger><TooltipContent>Generate QR code for online payment</TooltipContent></Tooltip>
                )}

                {!invoice.stripe_connected && invoice.invoice_token && !['paid', 'void', 'draft'].includes(invoice.status) && (
                  <Tooltip><TooltipTrigger asChild>
                    <span className="text-xs text-amber-600 flex items-center gap-1" data-testid="stripe-not-connected-badge">
                      <AlertCircle className="w-3.5 h-3.5" /> Online pay unavailable
                    </span>
                  </TooltipTrigger><TooltipContent>Connect your Stripe account in Settings to accept online payments</TooltipContent></Tooltip>
                )}

                {isEditable && invoice.status !== 'void' && (
                  <Tooltip><TooltipTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleVoid} data-testid="void-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Void</Button>
                  </TooltipTrigger><TooltipContent>Cancel this invoice permanently</TooltipContent></Tooltip>
                )}
              </div>
              {/* Mobile: kebab overflow menu */}
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8" data-testid="invoice-mobile-actions">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleDownloadPdf}>
                      <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
                    </DropdownMenuItem>
                    {canAddPayment && canEdit && (
                      <DropdownMenuItem onClick={() => setShowAddPayment(true)}>
                        <DollarSign className="w-3.5 h-3.5 mr-2" /> Add Payment
                      </DropdownMenuItem>
                    )}
                    {canAddPayment && canEdit && (
                      <DropdownMenuItem onClick={() => setShowMarkPaid(true)}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Mark Paid
                      </DropdownMenuItem>
                    )}
                    {canTakeOnlinePayment && (
                      <DropdownMenuItem onClick={() => setShowPaymentQR(true)}>
                        <Smartphone className="w-3.5 h-3.5 mr-2" /> Take Payment
                      </DropdownMenuItem>
                    )}
                    {isEditable && invoice.status !== 'void' && (
                      <DropdownMenuItem onClick={handleVoid} className="text-destructive">
                        <Ban className="w-3.5 h-3.5 mr-2" /> Void Invoice
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Draft guidance banner */}
          {invoice.status === 'draft' && isEditable && (
            <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5" data-testid="draft-guidance-banner">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <AlertCircle className="w-3.5 h-3.5 inline mr-1.5" />
                This invoice is a <strong>draft</strong> — review items, then send to your customer.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="text-amber-800 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/30"
                onClick={() => { setShowSendEmail(true); setSendEmail(invoice.billing_customer?.billing_email || customer?.billing_email || ''); }}
                data-testid="draft-send-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Send Invoice
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Sidebar */}
            <div className="space-y-4 lg:col-span-1 order-2 lg:order-1">
              {/* Customer Card */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4" /> Customer</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {isEditable ? (
                    <AsyncSearchableSelect value={invoice.customer_id || ''} onValueChange={(v) => updateField('customer_id', v)} searchFn={searchCustomers} placeholder="Search customers..." data-testid="invoice-customer-select" />
                  ) : (
                    <p className="text-sm font-medium" data-testid="invoice-customer-name">{invoice.customer_name || 'No customer'}</p>
                  )}
                  {customer && (
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {customer.primary_phone && <p>{customer.primary_phone}</p>}
                      {customer.billing_email && <p>{customer.billing_email}</p>}
                      {invoice.customer_location ? (
                        <LocationBadge location={invoice.customer_location} className="text-xs" />
                      ) : customer.service_street && (
                        <p>{customer.service_street}, {customer.service_city} {customer.service_state}</p>
                      )}
                    </div>
                  )}
                  {customer && isEditable && (
                    <LocationPicker
                      locations={customer.locations}
                      value={invoice.customer_location_id}
                      onChange={(locId) => updateField('customer_location_id', locId || '')}
                      className="mt-1"
                    />
                  )}
                  <BillingBadge billingCustomer={invoice.billing_customer} className="mt-2" />
                </CardContent>
              </Card>

              {/* Job Selector (optional) */}
              {invoice.customer_id && (
                <Card data-testid="invoice-job-card">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Briefcase className="w-4 h-4" /> Linked Job</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {isEditable ? (
                      <Select
                        value={invoice.job_id || '__none__'}
                        onValueChange={(v) => updateField('job_id', v === '__none__' ? '' : v)}
                        data-testid="invoice-job-select"
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="No job linked" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No job linked</SelectItem>
                          {loadingJobs ? (
                            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                          ) : (
                            customerJobs.map(j => (
                              <SelectItem key={j.id} value={j.id}>
                                #{j.job_number}{j.job_name ? ` — ${j.job_name}` : ''} ({j.status})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    ) : invoice.job_id ? (
                      <Button variant="link" size="sm" className="p-0 h-auto text-sm" onClick={() => navigate(`/jobs/${invoice.job_id}`)} data-testid="linked-job-btn">
                        View Job {invoice.job_name ? `— ${invoice.job_name}` : ''}
                      </Button>
                    ) : (
                      <p className="text-sm text-muted-foreground">No job linked</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Invoice Details */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="w-4 h-4" /> Invoice Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    {isEditable ? (
                      <Select value={invoice.status} onValueChange={(v) => updateField('status', v)} data-testid="invoice-status-select">
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTIONS.filter(s => s.value !== 'partially_paid').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm"><StatusBadge status={invoice.status} /></div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Due Date</Label>
                    {isEditable ? (
                      <Input type="date" value={invoice.due_date || ''} onChange={(e) => updateField('due_date', e.target.value || null)} data-testid="invoice-due-date" />
                    ) : (
                      <p className="text-sm">{invoice.due_date ? new Date(invoice.due_date + 'T00:00:00').toLocaleDateString() : 'No due date'}</p>
                    )}
                  </div>
                  {invoice.estimate_id && (
                    <div><Label className="text-xs text-muted-foreground">Linked Estimate</Label>
                    <Button variant="link" size="sm" className="p-0 h-auto text-sm" onClick={() => navigate(`/estimates/${invoice.estimate_id}`)} data-testid="linked-estimate">View Estimate</Button></div>
                  )}
                  {invoice.status === 'paid' && payoutData?.has_stripe_payment && payoutData.display && (
                    <div data-testid="invoice-deposit-inline"><Label className="text-xs text-muted-foreground">Deposit</Label><p className="text-sm">{payoutData.display.label}</p></div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
                <CardContent>
                  {isEditable ? (
                    <Textarea defaultValue={invoice.notes || ''} onBlur={(e) => updateField('notes', e.target.value)} placeholder="Internal notes..." rows={3} data-testid="invoice-notes" />
                  ) : (
                    <p className="text-sm text-muted-foreground">{invoice.notes || 'No notes'}</p>
                  )}
                </CardContent>
              </Card>

              {/* Allowed Payment Methods */}
              <Card data-testid="allowed-payment-methods-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Allowed Payment Methods</CardTitle></CardHeader>
                <CardContent className="space-y-2.5">
                  {(() => {
                    const methods = invoice.allowed_payment_methods || { card: true, cash: true, check: true, online: true, ach: false };
                    const isPaid = invoice.status === 'paid' || invoice.status === 'void';
                    const onlineEnabled = !!methods.online;
                    const MAIN_METHODS = [
                      { key: 'card',   label: 'Card (in-person)',      desc: 'Tech charges card on-site' },
                      { key: 'cash',   label: 'Cash',                  desc: 'Cash collection by tech' },
                      { key: 'check',  label: 'Check',                 desc: 'Check collection by tech' },
                      { key: 'online', label: 'Online / Payment Link', desc: 'Customer pays via Stripe link' },
                    ];
                    const toggleMethod = (key) => {
                      if (isPaid) return;
                      const next = { ...methods, [key]: !methods[key] };
                      // When online is toggled OFF, also disable ach
                      if (key === 'online' && !next.online) {
                        next.ach = false;
                      }
                      if (!next.card && !next.cash && !next.check && !next.online && !next.ach) {
                        toast.error('At least one payment method must be enabled');
                        return;
                      }
                      updateField('allowed_payment_methods', next);
                    };
                    return (
                      <>
                        {MAIN_METHODS.map(m => (
                          <label
                            key={m.key}
                            className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                              methods[m.key] ? 'border-primary/20 bg-primary/[0.03]' : 'border-muted-foreground/15 bg-muted/30 opacity-60'
                            } ${isPaid ? 'cursor-default' : 'hover:bg-accent/40'}`}
                            data-testid={`payment-method-toggle-${m.key}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded border-muted-foreground/30 h-4 w-4 accent-primary"
                              checked={!!methods[m.key]}
                              onChange={() => toggleMethod(m.key)}
                              disabled={isPaid}
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium leading-tight">{m.label}</p>
                              <p className="text-[10px] text-muted-foreground leading-tight">{m.desc}</p>
                            </div>
                          </label>
                        ))}
                        {/* ACH sub-toggle — nested under Online */}
                        <label
                          className={`flex items-center gap-3 p-2.5 ml-5 rounded-lg border cursor-pointer transition-colors ${
                            !onlineEnabled ? 'border-muted-foreground/10 bg-muted/20 opacity-40 cursor-not-allowed' :
                            methods.ach ? 'border-primary/20 bg-primary/[0.03]' : 'border-muted-foreground/15 bg-muted/30 opacity-60'
                          } ${isPaid ? 'cursor-default' : onlineEnabled ? 'hover:bg-accent/40' : ''}`}
                          data-testid="payment-method-toggle-ach"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-muted-foreground/30 h-4 w-4 accent-primary"
                            checked={!!methods.ach}
                            onChange={() => toggleMethod('ach')}
                            disabled={isPaid || !onlineEnabled}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium leading-tight">ACH Bank Transfer</p>
                            <p className="text-[10px] text-muted-foreground leading-tight">
                              {onlineEnabled ? 'Customer pays via bank transfer (Stripe)' : 'Enable Online to use ACH'}
                            </p>
                          </div>
                        </label>
                      </>
                    );
                  })()}
                  {(invoice.status === 'paid' || invoice.status === 'void') && (
                    <p className="text-[10px] text-muted-foreground italic">Payment methods are read-only on {invoice.status} invoices.</p>
                  )}
                </CardContent>
              </Card>

              {invoice.status === 'paid' && payoutData?.has_stripe_payment && <PayoutStatusCard data={payoutData} />}

              {/* Send Invoice */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" /> Send Invoice</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {invoice.sent_at && (
                    <div className="text-xs text-muted-foreground" data-testid="sent-status"><CheckCircle2 className="w-3 h-3 inline mr-1 text-green-500" />Sent to {invoice.sent_to} — {new Date(invoice.sent_at).toLocaleDateString()}</div>
                  )}
                  {invoice.view_count > 0 && (
                    <div className="text-xs text-muted-foreground" data-testid="invoice-view-tracking">
                      Opened {invoice.view_count} time{invoice.view_count !== 1 ? 's' : ''}
                      {invoice.last_viewed_at && <> &middot; last {new Date(invoice.last_viewed_at).toLocaleDateString()}</>}
                    </div>
                  )}
                  {invoice.invoice_token && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={handleCopyLink} data-testid="copy-link-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy Link</Button>
                      <Button variant="outline" size="sm" onClick={() => window.open(`/invoice/${invoice.invoice_token}`, '_blank')} data-testid="preview-link-btn"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                    </div>
                  )}
                  {!showSendEmail ? (
                    <Button size="sm" onClick={() => { setShowSendEmail(true); setSendEmail(invoice.billing_customer?.billing_email || customer?.billing_email || ''); }} data-testid="send-email-btn"><Send className="w-4 h-4 mr-1" /> Send by Email</Button>
                  ) : (
                    <div className="space-y-2">
                      <Input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="customer@email.com" data-testid="send-email-input" />
                      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="rounded" data-testid="attach-pdf-checkbox" /> Attach PDF
                      </label>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSendEmail} disabled={sending} data-testid="confirm-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send</Button>
                        <Button variant="outline" size="sm" onClick={() => setShowSendEmail(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 order-1 lg:order-2">
              {/* Line Items */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Line Items</CardTitle>
                    {isEditable && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setPbPickerOpen(true)} data-testid="add-from-pricebook-btn"><BookOpen className="w-3.5 h-3.5 mr-1" /> Price Book</Button>
                        <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={() => startNewItem('service')} data-testid="add-service-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Service</Button></TooltipTrigger><TooltipContent>Add a service line item</TooltipContent></Tooltip>
                        <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={() => startNewItem('material')} data-testid="add-material-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Material</Button></TooltipTrigger><TooltipContent>Add a material line item</TooltipContent></Tooltip>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {items.length === 0 && !newItem && (
                    <div className="text-center py-8 text-muted-foreground" data-testid="no-items">
                      <Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No line items yet</p>{isEditable && <p className="text-xs mt-1">Add services or materials above</p>}
                    </div>
                  )}
                  {(items.length > 0 || newItem) && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="items-table">
                        <thead><tr className="border-b text-xs text-muted-foreground"><th className="text-left py-2 pr-2 w-12">Type</th><th className="text-left py-2 pr-2">Description</th><th className="text-right py-2 pr-2 w-20">Qty</th><th className="text-right py-2 pr-2 w-24">Unit Price</th><th className="text-right py-2 pr-2 w-16">Disc%</th><th className="text-right py-2 pr-2 w-24">Total</th><th className="text-center py-2 pr-2 w-12">Tax</th>{isEditable && <th className="w-16"></th>}</tr></thead>
                        <tbody>
                          {items.map(item => editingItemId === item.id ? (
                            <ItemEditRow key={item.id} form={itemForm} setForm={setItemForm} onSave={saveEditItem} onCancel={() => setEditingItemId(null)} />
                          ) : (
                            <tr key={item.id} className="border-b last:border-0 group" data-testid={`item-row-${item.id}`}>
                              <td className="py-2.5 pr-2"><Badge variant="outline" className="text-[10px] capitalize">{item.type}</Badge></td>
                              <td className="py-2.5 pr-2"><div className="font-medium">{item.name}</div>{item.description && <div className="text-xs text-muted-foreground">{item.description}</div>}</td>
                              <td className="py-2.5 pr-2 text-right">{item.quantity}</td>
                              <td className="py-2.5 pr-2 text-right">${item.unit_price.toFixed(2)}</td>
                              <td className="py-2.5 pr-2 text-right text-xs text-muted-foreground">{item.discount_percent > 0 ? `${item.discount_percent}%` : ''}</td>
                              <td className="py-2.5 pr-2 text-right font-medium" data-testid={`item-total-${item.id}`}>${item.total_price.toFixed(2)}</td>
                              <td className="py-2.5 pr-2 text-center">{item.taxable !== false ? <span className="text-[10px] text-green-600">TAX</span> : <span className="text-[10px] text-muted-foreground/40">—</span>}</td>
                              {isEditable && (<td className="py-2.5 text-right"><div className="opacity-0 group-hover:opacity-100 flex gap-1 justify-end">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditItem(item)} data-testid={`edit-item-${item.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(item.id)} data-testid={`delete-item-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div></td>)}
                            </tr>
                          ))}
                          {newItem && <ItemEditRow form={newItem} setForm={setNewItem} onSave={saveNewItem} onCancel={() => setNewItem(null)} isNew />}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Totals & Balance */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-col items-end gap-1" data-testid="invoice-totals">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-medium w-24 text-right" data-testid="subtotal">${invoice.subtotal.toFixed(2)}</span>
                    </div>
                    {/* Document-level discount */}
                    {isEditable ? (
                      <div className="flex items-center gap-2 text-sm" data-testid="discount-controls">
                        <span className="text-muted-foreground">Discount</span>
                        <Select value={invoice.discount_type || 'none'} onValueChange={(v) => updateField('discount_type', v === 'none' ? null : v)}>
                          <SelectTrigger className="w-28 h-7 text-xs" data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="percentage">%</SelectItem>
                            <SelectItem value="fixed">$ Fixed</SelectItem>
                          </SelectContent>
                        </Select>
                        {invoice.discount_type && (
                          <>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={invoice.discount_value || ''}
                              onChange={(e) => updateField('discount_value', parseFloat(e.target.value) || 0)}
                              className="w-20 text-right text-xs h-7"
                              data-testid="discount-value-input"
                            />
                            <span className="text-xs text-muted-foreground">{invoice.discount_type === 'percentage' ? '%' : '$'}</span>
                            <span className="text-xs font-medium text-red-600 w-24 text-right" data-testid="discount-total">-${(invoice.discount_total || 0).toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    ) : (invoice.discount_total > 0 && (
                      <div className="flex items-center gap-4 text-sm text-red-600">
                        <span>Discount {invoice.discount_type === 'percentage' ? `(${invoice.discount_value}%)` : ''}</span>
                        <span className="font-medium w-24 text-right" data-testid="discount-total">-${invoice.discount_total.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">Tax</span>
                      {isEditable ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              step="0.001"
                              min="0"
                              value={invoice.tax_rate ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? -1 : parseFloat(e.target.value);
                                updateField('tax_rate', val);
                              }}
                              placeholder="Rate"
                              className="w-20 text-right text-xs h-7"
                              data-testid="tax-rate-input"
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                          <span className="text-xs font-medium w-24 text-right" data-testid="tax-amount">${invoice.tax_amount.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {invoice.tax_rate != null && <span className="text-xs text-muted-foreground">({invoice.tax_rate}%)</span>}
                          <span className="font-medium w-24 text-right">${invoice.tax_amount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="border-t pt-1 mt-1 flex items-center gap-4 text-base">
                      <span className="font-semibold">Total</span>
                      <span className="font-bold w-24 text-right" data-testid="total">${invoice.total.toFixed(2)}</span>
                    </div>
                    {invoice.total_paid > 0 && (
                      <div className="flex items-center gap-4 text-sm text-green-600">
                        <span>Paid</span>
                        <span className="font-medium w-24 text-right" data-testid="total-paid">-${invoice.total_paid.toFixed(2)}</span>
                      </div>
                    )}
                    {(invoice.balance_due !== invoice.total || invoice.total_paid > 0) && (
                      <div className="border-t pt-1 mt-1 flex items-center gap-4 text-base">
                        <span className="font-semibold">Balance Due</span>
                        <span className={`font-bold w-24 text-right ${invoice.balance_due > 0 ? 'text-amber-600' : 'text-green-600'}`} data-testid="balance-due">
                          ${invoice.balance_due.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Payment History */}
              {payments.length > 0 && (
                <Card data-testid="payment-history-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Payment History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {payments.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/20 group" data-testid={`payment-row-${p.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0">
                              <PaymentMethodBadge method={p.payment_method} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm" data-testid={`payment-amount-${p.id}`}>${p.amount.toFixed(2)}</span>
                                {p.reference_number && <span className="text-xs text-muted-foreground">#{p.reference_number}</span>}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                                {p.notes && <span> — {p.notes}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {p.stripe_payment_intent_id && (
                              <span className="text-[10px] text-muted-foreground/60 mr-1">Stripe</span>
                            )}
                            {canEdit && !p.stripe_payment_intent_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => { setReverseTarget(p); setReverseReason(''); }}
                                data-testid={`reverse-payment-${p.id}`}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" /> Reverse
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Status Correction — shown when paid with no payments or balance mismatch */}
              {canEdit && invoice.status === 'paid' && payments.length === 0 && (
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-800" data-testid="status-correction-card">
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 min-w-0">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span className="text-sm">This invoice is marked paid but has no payment records.</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                        onClick={handleCorrectStatus}
                        data-testid="correct-status-btn"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Correct Status
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Audit Log */}
              {auditLog.length > 0 && (
                <Card data-testid="audit-log-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4" /> Correction History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {auditLog.map(entry => (
                        <div key={entry.id} className="flex items-start gap-2 text-xs text-muted-foreground py-1.5 border-b last:border-0" data-testid={`audit-entry-${entry.id}`}>
                          <RefreshCw className="w-3 h-3 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <span className="font-medium text-foreground">
                              {entry.action === 'payment_reversed' && `Payment of $${entry.payment_amount?.toFixed(2)} reversed`}
                              {entry.action === 'status_corrected' && `Status corrected: ${entry.details?.old_status} → ${entry.details?.new_status}`}
                            </span>
                            {entry.reason && <span className="ml-1">— {entry.reason}</span>}
                            <span className="ml-1">by {entry.performed_by_name}</span>
                            {entry.created_at && <span className="ml-1">{new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Paid Banner */}
              {invoice.status === 'paid' && (
                <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10 dark:border-green-800">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400" data-testid="paid-banner">
                      <CheckCircle2 className="w-5 h-5" /><span className="font-medium text-sm">Fully Paid — ${invoice.total_paid.toFixed(2)} received</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </TooltipProvider>

      <PriceBookPicker open={pbPickerOpen} onOpenChange={setPbPickerOpen} targetType="invoice" targetId={id} onAdd={() => fetchInvoice()} />
      <Dialog open={showPaymentQR} onOpenChange={setShowPaymentQR}>
        <DialogContent className="!max-w-[calc(100vw-2rem)] sm:!max-w-md !p-4 sm:!p-6" data-testid="take-payment-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" /> Take Payment</DialogTitle></DialogHeader>
          <TakePaymentContent invoice={invoice} onClose={() => { setShowPaymentQR(false); fetchInvoice(); }} />
        </DialogContent>
      </Dialog>
      <AddPaymentModal open={showAddPayment} onOpenChange={setShowAddPayment} invoice={invoice} onSuccess={fetchInvoice} />
      <MarkPaidModal open={showMarkPaid} onOpenChange={setShowMarkPaid} invoice={invoice} onSuccess={fetchInvoice} />

      {/* Payment Reversal Confirmation */}
      <AlertDialog open={!!reverseTarget} onOpenChange={(open) => { if (!open) { setReverseTarget(null); setReverseReason(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-destructive" /> Reverse Payment
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>This will remove the recorded payment and recalculate the invoice balance.</p>
                {reverseTarget && (
                  <div className="rounded-lg border p-3 bg-muted/30 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">${reverseTarget.amount?.toFixed(2)}</span>
                      <span className="text-xs capitalize">{reverseTarget.payment_method}</span>
                    </div>
                    {reverseTarget.payment_date && (
                      <p className="text-xs">{new Date(reverseTarget.payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    )}
                    {reverseTarget.reference_number && <p className="text-xs">Ref: #{reverseTarget.reference_number}</p>}
                  </div>
                )}
                <div>
                  <Label className="text-xs font-medium text-foreground">Reason for reversal (optional)</Label>
                  <Textarea
                    value={reverseReason}
                    onChange={(e) => setReverseReason(e.target.value)}
                    placeholder="e.g., Payment was entered in error, wrong amount, duplicate entry..."
                    rows={2}
                    className="mt-1 text-sm"
                    data-testid="reverse-reason-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reverse">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReversePayment}
              disabled={reversing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-reverse-payment"
            >
              {reversing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Reverse Payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function ItemEditRow({ form, setForm, onSave, onCancel, isNew = false }) {
  const disc = parseFloat(form.discount_percent) || 0;
  const lineTotal = ((parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0) * (1 - disc / 100)).toFixed(2);
  return (
    <tr className="border-b bg-muted/30" data-testid={isNew ? 'new-item-row' : 'edit-item-row'}>
      <td className="py-2 pr-2"><Badge variant="outline" className="text-[10px] capitalize">{form.type}</Badge></td>
      <td className="py-2 pr-2 space-y-1">
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Item name" className="h-7 text-sm" data-testid="item-name-input" autoFocus />
        <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" className="h-7 text-sm" data-testid="item-description-input" />
      </td>
      <td className="py-2 pr-2"><Input type="number" step="0.01" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-sm text-right w-20" data-testid="item-qty-input" /></td>
      <td className="py-2 pr-2"><Input type="number" step="0.01" min="0" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })} className="h-7 text-sm text-right w-24" data-testid="item-price-input" /></td>
      <td className="py-2 pr-2"><Input type="number" step="0.1" min="0" max="100" value={form.discount_percent || ''} onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) || 0 })} placeholder="0" className="h-7 text-sm text-right w-16" data-testid="item-discount-input" /></td>
      <td className="py-2 pr-2 text-right font-medium text-sm" data-testid="item-line-total">${lineTotal}</td>
      <td className="py-2 pr-2 text-center">
        <input type="checkbox" checked={form.taxable !== false} onChange={(e) => setForm({ ...form, taxable: e.target.checked })} className="rounded border-muted-foreground/30" data-testid="item-taxable-check" />
      </td>
      <td className="py-2 text-right">
        <div className="flex gap-1 justify-end">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onSave} data-testid="save-item-btn"><Save className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel} data-testid="cancel-item-btn"><X className="w-3.5 h-3.5" /></Button>
        </div>
      </td>
    </tr>
  );
}
